import { useState, useEffect, useRef } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import API from '../api';

function Messages() {
  const { userInfo } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    if (selectedContact) fetchMessages(selectedContact._id);
  }, [selectedContact]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchContacts = async () => {
    try {
      const { data } = await API.get('/messages/contacts');
      setContacts(data);
    } catch (error) {
      console.error('Error fetching contacts');
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const { data } = await API.get(`/messages/${userId}`);
      setMessages(data);
    } catch (error) {
      console.error('Error fetching messages');
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedContact) return;
    setSending(true);
    try {
      await API.post('/messages', {
        receiver: selectedContact._id,
        content: newMessage
      });
      setNewMessage('');
      fetchMessages(selectedContact._id);
    } catch (error) {
      console.error('Error sending message');
    }
    setSending(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <AdminLayout>
      <div style={styles.header}>
        <h1 style={styles.heading}>Messages</h1>
        <p style={styles.subheading}>Communicate with your clients</p>
      </div>

      <div style={styles.chatContainer}>
        {/* Contacts List */}
        <div style={styles.contactsList}>
          <div style={styles.contactsHeader}>Contacts</div>
          {contacts.length === 0 ? (
            <p style={styles.noContacts}>No contacts yet</p>
          ) : (
            contacts.map((contact) => (
              <div
                key={contact._id}
                style={{
                  ...styles.contactItem,
                  backgroundColor: selectedContact?._id === contact._id
                    ? '#f0f4ff' : 'white'
                }}
                onClick={() => setSelectedContact(contact)}
              >
                <div style={styles.contactAvatar}>
                  {contact.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p style={styles.contactName}>{contact.name}</p>
                  <p style={styles.contactRole}>{contact.role}</p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Chat Window */}
        <div style={styles.chatWindow}>
          {!selectedContact ? (
            <div style={styles.noChat}>
              <p>👈 Select a contact to start messaging</p>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div style={styles.chatHeader}>
                <div style={styles.contactAvatar}>
                  {selectedContact.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p style={styles.chatName}>{selectedContact.name}</p>
                  <p style={styles.chatRole}>{selectedContact.role}</p>
                </div>
              </div>

              {/* Messages */}
              <div style={styles.messagesArea}>
                {messages.length === 0 ? (
                  <p style={styles.noMessages}>
                    No messages yet. Say hello! 👋
                  </p>
                ) : (
                  messages.map((msg) => {
                    const isMe = msg.sender._id === userInfo._id ||
                      msg.sender.name === userInfo.name;
                    return (
                      <div
                        key={msg._id}
                        style={{
                          ...styles.messageBubbleWrapper,
                          justifyContent: isMe ? 'flex-end' : 'flex-start'
                        }}
                      >
                        <div style={{
                          ...styles.messageBubble,
                          backgroundColor: isMe ? '#4f46e5' : '#f1f5f9',
                          color: isMe ? 'white' : '#1a1a2e'
                        }}>
                          <p style={styles.messageText}>{msg.content}</p>
                          <p style={{
                            ...styles.messageTime,
                            color: isMe ? '#c7d2fe' : '#94a3b8'
                          }}>
                            {new Date(msg.createdAt).toLocaleTimeString([], {
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div style={styles.inputArea}>
                <textarea
                  style={styles.messageInput}
                  placeholder="Type a message... (Enter to send)"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  rows={2}
                />
                <button
                  style={styles.sendBtn}
                  onClick={handleSend}
                  disabled={sending}
                >
                  {sending ? '...' : '➤'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

const styles = {
  header: { marginBottom: '24px' },
  heading: { margin: '0', fontSize: '28px', fontWeight: '700', color: '#1a1a2e' },
  subheading: { margin: '4px 0 0 0', color: '#718096', fontSize: '15px' },
  chatContainer: {
    display: 'flex', height: '70vh', backgroundColor: 'white',
    borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    overflow: 'hidden'
  },
  contactsList: {
    width: '280px', borderRight: '1px solid #e2e8f0',
    overflowY: 'auto', flexShrink: 0
  },
  contactsHeader: {
    padding: '16px 20px', fontWeight: '700', fontSize: '14px',
    color: '#718096', borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f7f8fc'
  },
  noContacts: { padding: '20px', color: '#718096', fontSize: '14px', textAlign: 'center' },
  contactItem: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 20px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0'
  },
  contactAvatar: {
    width: '38px', height: '38px', borderRadius: '50%',
    backgroundColor: '#4f46e5', color: 'white', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: '16px', fontWeight: '700', flexShrink: 0
  },
  contactName: { margin: '0', fontSize: '14px', fontWeight: '600', color: '#1a1a2e' },
  contactRole: { margin: '2px 0 0 0', fontSize: '12px', color: '#718096' },
  chatWindow: { flex: 1, display: 'flex', flexDirection: 'column' },
  noChat: {
    flex: 1, display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#718096', fontSize: '16px'
  },
  chatHeader: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '16px 20px', borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f7f8fc'
  },
  chatName: { margin: '0', fontSize: '15px', fontWeight: '700', color: '#1a1a2e' },
  chatRole: { margin: '2px 0 0 0', fontSize: '12px', color: '#718096' },
  messagesArea: {
    flex: 1, overflowY: 'auto', padding: '20px',
    display: 'flex', flexDirection: 'column', gap: '8px'
  },
  noMessages: { textAlign: 'center', color: '#718096', marginTop: '40px' },
  messageBubbleWrapper: { display: 'flex' },
  messageBubble: {
    maxWidth: '60%', padding: '10px 14px',
    borderRadius: '12px', wordBreak: 'break-word'
  },
  messageText: { margin: '0', fontSize: '14px', lineHeight: '1.5' },
  messageTime: { margin: '4px 0 0 0', fontSize: '11px' },
  inputArea: {
    display: 'flex', gap: '8px', padding: '16px',
    borderTop: '1px solid #e2e8f0', alignItems: 'flex-end'
  },
  messageInput: {
    flex: 1, padding: '10px 14px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '14px', resize: 'none',
    fontFamily: 'inherit'
  },
  sendBtn: {
    padding: '10px 20px', backgroundColor: '#4f46e5', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '18px', fontWeight: '700'
  }
};

export default Messages;